"use client"

import { useEffect, useState, useCallback } from "react"
import { useTranslations } from "next-intl"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Plus, Trash2 } from "lucide-react"
import { DemoContent, DEMO_CLIPS } from "@/app/[locale]/monitoring/demo/demo-content"

interface Sandbox {
  id: string
  name: string
  createdAt: string
}

const SANDBOXES_KEY = "mineguard_sandboxes"
const ACTIVE_KEY = "mineguard_active_sandbox"
const OLD_KEY = "mineguard_sandbox_id"

function generateId(): string {
  return `sandbox_${crypto.randomUUID().slice(0, 8)}`
}

function loadSandboxes(): Sandbox[] {
  try {
    const raw = localStorage.getItem(SANDBOXES_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }

  const oldId = localStorage.getItem(OLD_KEY)
  if (oldId) {
    const migrated: Sandbox[] = [{ id: oldId, name: "Sandbox 1", createdAt: new Date().toISOString() }]
    localStorage.setItem(SANDBOXES_KEY, JSON.stringify(migrated))
    localStorage.setItem(ACTIVE_KEY, oldId)
    localStorage.removeItem(OLD_KEY)
    return migrated
  }

  return []
}

function saveSandboxes(list: Sandbox[]) {
  localStorage.setItem(SANDBOXES_KEY, JSON.stringify(list))
}

export default function TryRecordingPage() {
  const t = useTranslations("monitoringDemo")
  const [sandboxes, setSandboxes] = useState<Sandbox[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let list = loadSandboxes()

    if (list.length === 0) {
      const first: Sandbox = { id: generateId(), name: t("sandbox.title", { n: 1 }), createdAt: new Date().toISOString() }
      list = [first]
      saveSandboxes(list)
    }

    setSandboxes(list)

    const saved = localStorage.getItem(ACTIVE_KEY)
    const active = list.find(s => s.id === saved) ? saved! : list[0].id
    setActiveId(active)
    localStorage.setItem(ACTIVE_KEY, active)

    fetch("/api/sandbox/log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sandboxId: active }),
    }).catch(() => {})

    setReady(true)
  }, [])

  const createSandbox = useCallback(() => {
    const newSandbox: Sandbox = {
      id: generateId(),
      name: t("sandbox.title", { n: sandboxes.length + 1 }),
      createdAt: new Date().toISOString(),
    }
    const updated = [...sandboxes, newSandbox]
    setSandboxes(updated)
    saveSandboxes(updated)
    setActiveId(newSandbox.id)
    localStorage.setItem(ACTIVE_KEY, newSandbox.id)

    fetch("/api/sandbox/log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sandboxId: newSandbox.id }),
    }).catch(() => {})
  }, [sandboxes, t])

  const switchSandbox = useCallback((id: string) => {
    setActiveId(id)
    localStorage.setItem(ACTIVE_KEY, id)
  }, [])

  const deleteSandbox = useCallback(() => {
    if (!activeId || sandboxes.length <= 1) return
    localStorage.removeItem(`mineguard_custom_engines_${activeId}`)
    const remaining = sandboxes.filter(s => s.id !== activeId)
    setSandboxes(remaining)
    saveSandboxes(remaining)
    const nextId = remaining[0].id
    setActiveId(nextId)
    localStorage.setItem(ACTIVE_KEY, nextId)
  }, [activeId, sandboxes])

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <Tabs defaultValue="preloaded" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="preloaded">{t("tabs.preloaded")}</TabsTrigger>
          <TabsTrigger value="yours">{t("tabs.yours")}</TabsTrigger>
        </TabsList>

        <TabsContent value="preloaded" className="mt-0">
          <DemoContent
            workspace="default"
            demoClips={DEMO_CLIPS}
            allowEnrollment={false}
            variant="showcase"
            title={t("showcase.title")}
            subtitle={t("showcase.subtitle")}
          />
        </TabsContent>

        <TabsContent value="yours" className="mt-0">
          {ready && activeId ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <Select value={activeId} onValueChange={(v) => v && switchSandbox(v)}>
                  <SelectTrigger className="w-[180px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sandboxes.map(s => (
                      <SelectItem key={s.id} value={s.id} className="text-xs">
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={createSandbox} className="h-8 gap-1 text-xs">
                  <Plus className="h-3 w-3" />
                  {t("sandbox.newButton")}
                </Button>
                {sandboxes.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={deleteSandbox} className="h-8 text-xs text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
                <span className="ml-auto text-[10px] font-mono text-muted-foreground">{activeId}</span>
              </div>

              <DemoContent
                key={activeId}
                workspace={activeId}
                demoClips={[]}
                allowEnrollment={true}
                variant="testing"
                title={t("testing.title")}
                subtitle={t("testing.subtitle")}
              />
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground text-sm">{t("sandbox.loading")}</div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
